import {
   Alert,
   AlertDescription,
   AlertTitle,
   Badge,
   Box,
   Button,
   Drawer,
   DrawerBody,
   DrawerCloseButton,
   DrawerContent,
   DrawerFooter,
   DrawerHeader,
   DrawerOverlay,
   Flex,
   Heading,
   HStack,
   SimpleGrid,
   Skeleton,
   Spinner,
   Text,
   VisuallyHidden,
   VStack,
} from '@chakra-ui/react';
import { faCircleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { usePriceTier } from '@ifixit/app/hooks';
import { executeNextLoop, formatMoney } from '@ifixit/helpers';
import { useTranslations } from '@ifixit/i18n/hooks';
import type { LocaleCode } from '@ifixit/i18n/types';
import { FaIcon } from '@ifixit/icons';
import { useCart } from '@ifixit/shopify-cart-sdk/hooks';
import { useEffect, useState, type ElementType } from 'react';
import { Collapse, Fade, Slide } from '../../animations';
import { FreeShippingBanner } from '../../commerce/free-shipping-banner';
import { useIsMountedState } from '../../hooks';
import { MerchBanner } from '../../merch/merch-banner';
import { CartLineItems } from '../cart-line-items';
import { CrossSell } from '../cross-sell';
import { useCrossSellItems } from '../cross-sell/hooks/use-cross-sell-items';
import type { CartImageComponent } from '../types';
import { CartDrawerTrigger } from './CartDrawerTrigger';
import { CartEmptyState } from './CartEmptyState';
import { useCartDrawer } from './hooks/useCartDrawer';
import { ShopifyCheckoutButton } from './shopify-checkout-button';

interface CartDrawerProps {
   ImageComponent: CartImageComponent;
   linkComponent?: ElementType;
   supportLinkComponent: ElementType;
   localeCode: LocaleCode;
   viewCartUrl: string;
   storeCode: string;
   showMerchBanner?: boolean;
   recordMerchExperiment: () => boolean;
}

export function CartDrawer({
   ImageComponent,
   linkComponent,
   localeCode,
   supportLinkComponent: SupportLinkComponent,
   viewCartUrl,
   storeCode,
   recordMerchExperiment,
}: CartDrawerProps) {
   const { isOpen, onOpen, onClose, onViewCart } = useCartDrawer();
   const isMounted = useIsMountedState();
   const cart = useCart();
   const cartData = cart.data ?? null;
   const t = useTranslations();
   const { priceTier } = usePriceTier({ storeCode });
   const crossSellItems = useCrossSellItems({ localeCode, shouldQuery: isOpen });
   const [showMerchBanner, setShowMerchBanner] = useState(false);
   const isCartEmpty = cart.isFetched && !cartData?.hasItemsInCart;

   useEffect(() => {
      if (isMounted && isOpen) {
         // eslint-disable-next-line react-hooks/set-state-in-effect
         setShowMerchBanner(recordMerchExperiment());
      }
   }, [isMounted, isOpen]);

   return (
      <>
         <CartDrawerTrigger
            onClick={() => executeNextLoop(onOpen)}
            hasItemsInCart={cartData?.hasItemsInCart}
         />
         {isMounted && (
            <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
               <DrawerOverlay />
               <DrawerContent
                  color="gray.800"
                  data-mobile-appid="cart-drawer"
                  data-testid="cart-drawer"
                  fontSize="md"
                  maxWidth={{ base: 'calc(100vw - 60px)', sm: '436px' }}
               >
                  <DrawerHeader
                     borderBottomWidth={1}
                     borderColor="gray.300"
                     position="relative"
                     px={3}
                  >
                     <DrawerCloseButton
                        top="50%"
                        transform="translateY(-50%)"
                        data-testid="cart-drawer-close"
                        color="#a3a3a3"
                        fontSize="12px"
                     />
                     <HStack align="center">
                        <Heading fontSize="16px" lineHeight="normal" my="0">
                           {t('CartPage.shoppingCart')}
                        </Heading>
                        {(cartData != null || !cart.isError) && (
                           <Badge
                              borderRadius="full"
                              variant="subtle"
                              colorScheme="gray"
                              boxSize="6"
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                              bg="gray.200"
                              color="gray.400"
                           >
                              {cart.isLoading ? (
                                 <Spinner size="xs" />
                              ) : (
                                 <>
                                    <VisuallyHidden>{t('CartPage.numItems')}</VisuallyHidden>
                                    <Box
                                       as="span"
                                       data-testid="cart-drawer-item-count"
                                       color="gray.500"
                                       fontWeight="700"
                                    >
                                       {cartData?.totals?.itemsCount ?? 0}
                                    </Box>
                                 </>
                              )}
                           </Badge>
                        )}
                     </HStack>
                  </DrawerHeader>

                  <DrawerBody p={3} data-testid="cart-drawer-body" position="relative">
                     <VStack gap={3} align="unset">
                        {cart.isError && (
                           <Alert
                              status="error"
                              variant="subtle"
                              flexDirection="column"
                              alignItems="center"
                              justifyContent="center"
                              textAlign="center"
                              height="200px"
                           >
                              <FaIcon icon={faCircleExclamation} h="10" color="red.500" />
                              <AlertTitle mt={3} mb={1} fontSize="lg">
                                 {t('CartPage.Error.fetch')}
                              </AlertTitle>
                              <AlertDescription maxWidth="sm">
                                 {t.rich('CartPage.Error.reload', {
                                    link: chunks => (
                                       <SupportLinkComponent>{chunks}</SupportLinkComponent>
                                    ),
                                 })}
                              </AlertDescription>
                           </Alert>
                        )}
                        {cartData?.hasItemsInCart && (
                           <>
                              <FreeShippingBanner
                                 localeCode={localeCode}
                                 storeCode={storeCode}
                                 priceTier={priceTier}
                              />
                              {showMerchBanner && (
                                 <MerchBanner
                                    analytics={{ descriptor: 'Cart Drawer Merch' }}
                                    ImageComponent={ImageComponent}
                                    linkComponent={linkComponent}
                                    localeCode={localeCode}
                                 />
                              )}
                              <CartLineItems
                                 cart={cartData}
                                 testIdPrefix="cart-drawer"
                                 borderBottomWidth={1}
                                 borderColor="gray.300"
                                 ImageComponent={ImageComponent}
                                 linkComponent={linkComponent}
                                 localeCode={localeCode}
                              />
                           </>
                        )}
                        <Fade show={isCartEmpty} disableExitAnimation style={{ width: '100%' }}>
                           <CartEmptyState onClose={onClose} />
                           {showMerchBanner && (
                              <Box>
                                 <MerchBanner
                                    analytics={{ descriptor: 'Cart Drawer Merch' }}
                                    ImageComponent={ImageComponent}
                                    linkComponent={linkComponent}
                                    localeCode={localeCode}
                                 />
                              </Box>
                           )}
                        </Fade>
                        <Box data-testid="cart-drawer-x-sell-items">
                           <CrossSell
                              analytics={{ descriptor: 'Cart Drawer Cross Sell' }}
                              crossSellItems={crossSellItems}
                              spacing="3"
                              ImageComponent={ImageComponent}
                              linkComponent={linkComponent}
                              localeCode={localeCode}
                           />
                        </Box>
                     </VStack>
                  </DrawerBody>

                  <Slide show={cartData?.hasItemsInCart}>
                     <DrawerFooter
                        borderTopWidth={1}
                        borderColor="gray.300"
                        p={3}
                        display={cartData?.hasItemsInCart ? 'flex' : 'none'}
                     >
                        <Box w="full">
                           <Collapse show={!cart.isError} style={{ marginBottom: '0.75rem' }}>
                              <Flex w="full" justify="space-between">
                                 <Text fontSize="14px" fontWeight="bold">
                                    {t('CartPage.total')}
                                 </Text>
                                 <Flex direction="column" align="flex-end">
                                    {cartData && !cart.isRefetching ? (
                                       <Text
                                          fontSize="20px"
                                          lineHeight="1em"
                                          fontWeight="bold"
                                          my="0"
                                       >
                                          {formatMoney(cartData.totals.price, localeCode)}
                                       </Text>
                                    ) : (
                                       <Skeleton h="20px" w="80px" />
                                    )}
                                 </Flex>
                              </Flex>
                           </Collapse>
                           <SimpleGrid columns={2} spacing="2.5" w="full">
                              <Button
                                 as="a"
                                 href={viewCartUrl}
                                 variant="solid"
                                 bgColor="gray.200"
                                 fontSize="16px"
                                 onClick={onViewCart}
                                 _hover={{ opacity: 0.8 }}
                              >
                                 {t('CartPage.viewCart')}
                              </Button>
                              <ShopifyCheckoutButton />
                           </SimpleGrid>
                        </Box>
                     </DrawerFooter>
                  </Slide>
               </DrawerContent>
            </Drawer>
         )}
      </>
   );
}
